import { supabase } from './supabase';

export interface ServiceWorkflow {
  id: string;
  service_id: string;
  business_id: string;
  name: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

export interface DefaultResource {
  id: string;
  name: string;
}

export interface WorkflowStep {
  id: string;
  workflow_id: string;
  title: string;
  description?: string;
  category?: string;
  workflow_step_library_id?: string;
  order_index: number;
  default_resource_id?: string; // Will store JSON array temporarily
  default_resources?: DefaultResource[];
  photos_enabled?: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateWorkflowData {
  service_id: string;
  business_id: string;
  name: string;
  description?: string;
  steps: Array<{
    title: string;
    description?: string;
    workflow_step_library_id?: string;
    order_index: number;
    default_resources?: DefaultResource[];
    photos_enabled?: boolean;
  }>;
}

export async function createWorkflow(data: CreateWorkflowData): Promise<ServiceWorkflow> {
  try {
    // First, check if a workflow already exists for this service
    const { data: existingWorkflow } = await supabase
      .from('service_workflows')
      .select('id')
      .eq('service_id', data.service_id)
      .single();

    if (existingWorkflow) {
      throw new Error('A workflow already exists for this service');
    }

    // Create the workflow
    const { data: workflow, error: workflowError } = await supabase
      .from('service_workflows')
      .insert({
        service_id: data.service_id,
        business_id: data.business_id,
        name: data.name,
        description: data.description,
      })
      .select()
      .single();

    if (workflowError) throw workflowError;

    // Create the steps
    if (data.steps.length > 0) {
      const stepsToInsert = data.steps.map(step => ({
        workflow_id: workflow.id,
        title: step.title,
        description: step.description,
        workflow_step_library_id: step.workflow_step_library_id,
        order_index: step.order_index,
        photos_enabled: step.photos_enabled || false,
        // Don't use default_resource_id anymore
      }));

      const { data: insertedSteps, error: stepsError } = await supabase
        .from('workflow_steps')
        .insert(stepsToInsert)
        .select();

      if (stepsError) {
        // If steps fail, delete the workflow
        await supabase
          .from('service_workflows')
          .delete()
          .eq('id', workflow.id);
        throw stepsError;
      }

      // Now insert default resources into junction table
      const defaultResourcesEntries: any[] = [];
      
      if (insertedSteps) {
        insertedSteps.forEach((insertedStep, index) => {
          const stepData = data.steps[index];
          if (stepData.default_resources && stepData.default_resources.length > 0) {
            stepData.default_resources.forEach(resource => {
              defaultResourcesEntries.push({
                workflow_step_id: insertedStep.id,
                resource_id: resource.id
              });
            });
          }
        });
      }

      if (defaultResourcesEntries.length > 0) {
        const { error: resourcesError } = await supabase
          .from('workflow_step_default_resources')
          .insert(defaultResourcesEntries);
        
        if (resourcesError) {
          console.error('Error inserting default resources:', resourcesError);
          // If the table doesn't exist yet, show a more helpful message
          if (resourcesError.message?.includes('does not exist')) {
            console.error('Junction table workflow_step_default_resources does not exist. Please run the migration: add_workflow_step_default_resources_table.sql');
          }
          // Don't fail the whole operation if default resources fail
        }
      }
    }

    return workflow;
  } catch (error) {
    console.error('Error creating workflow:', error);
    throw error;
  }
}

export async function getWorkflowByServiceId(serviceId: string): Promise<ServiceWorkflow | null> {
  try {
    const { data, error } = await supabase
      .from('service_workflows')
      .select('*')
      .eq('service_id', serviceId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No workflow found
        return null;
      }
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error fetching workflow:', error);
    throw error;
  }
}

export async function getWorkflowSteps(workflowId: string): Promise<WorkflowStep[]> {
  try {
    // First try simple query without the join
    const { data, error } = await supabase
      .from('workflow_steps_with_details')
      .select('*')
      .eq('workflow_id', workflowId)
      .order('order_index', { ascending: true });

    if (error) {
      // If view doesn't exist, try the regular table
      if (error.message?.includes('does not exist')) {
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('workflow_steps')
          .select('*')
          .eq('workflow_id', workflowId)
          .order('order_index', { ascending: true });
        
        if (fallbackError) throw fallbackError;
        return fallbackData || [];
      }
      throw error;
    }

    // Fetch default resources from junction table
    if (data && data.length > 0) {
      const stepIds = data.map(step => step.id);
      
      // Get default resources for all steps
      const { data: defaultResourcesData, error: resourcesError } = await supabase
        .from('workflow_step_default_resources')
        .select(`
          workflow_step_id,
          resource:resources(
            id,
            first_name,
            last_name
          )
        `)
        .in('workflow_step_id', stepIds);
      
      if (resourcesError) {
        console.error('Error fetching default resources:', resourcesError);
        if (resourcesError.message?.includes('does not exist')) {
          console.error('Junction table workflow_step_default_resources does not exist. Please run the migration: add_workflow_step_default_resources_table.sql');
        }
        // Continue without default resources if table doesn't exist
      }
      
      // Group resources by step ID
      const resourcesByStep = new Map<string, DefaultResource[]>();
      
      if (defaultResourcesData) {
        defaultResourcesData.forEach(entry => {
          if (entry.resource) {
            const stepId = entry.workflow_step_id;
            if (!resourcesByStep.has(stepId)) {
              resourcesByStep.set(stepId, []);
            }
            resourcesByStep.get(stepId)?.push({
              id: entry.resource.id,
              name: `${entry.resource.first_name} ${entry.resource.last_name}`
            });
          }
        });
      }
      
      // Add default resources to each step
      return data.map(step => {
        return {
          ...step,
          default_resources: resourcesByStep.get(step.id) || []
        };
      });
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching workflow steps:', error);
    throw error;
  }
}

export async function updateWorkflow(
  workflowId: string,
  data: {
    name?: string;
    description?: string;
    is_active?: boolean;
    steps?: Array<{
      id?: string;
      title: string;
      description?: string;
      workflow_step_library_id?: string;
      order_index: number;
      default_resources?: DefaultResource[];
      photos_enabled?: boolean;
    }>;
  }
): Promise<ServiceWorkflow> {
  try {
    // Update the workflow itself
    const { data: workflow, error } = await supabase
      .from('service_workflows')
      .update({
        name: data.name,
        description: data.description,
        is_active: data.is_active
      })
      .eq('id', workflowId)
      .select()
      .single();

    if (error) throw error;

    // If steps are provided, update them
    if (data.steps) {
      // Delete existing steps
      const { error: deleteError } = await supabase
        .from('workflow_steps')
        .delete()
        .eq('workflow_id', workflowId);

      if (deleteError) throw deleteError;

      // Insert new steps
      if (data.steps.length > 0) {
        const stepsToInsert = data.steps.map(step => ({
          workflow_id: workflowId,
          title: step.title,
          description: step.description,
          workflow_step_library_id: step.workflow_step_library_id,
          order_index: step.order_index,
          photos_enabled: step.photos_enabled || false,
          // Don't use default_resource_id anymore
        }));

        const { data: insertedSteps, error: stepsError } = await supabase
          .from('workflow_steps')
          .insert(stepsToInsert)
          .select();

        if (stepsError) throw stepsError;

        // Delete old default resources for this workflow's steps
        const { error: deleteResourcesError } = await supabase
          .from('workflow_step_default_resources')
          .delete()
          .in('workflow_step_id', insertedSteps?.map(s => s.id) || []);
        
        if (deleteResourcesError) {
          console.error('Error deleting old default resources:', deleteResourcesError);
          if (deleteResourcesError.message?.includes('does not exist')) {
            console.error('Junction table workflow_step_default_resources does not exist. Please run the migration: add_workflow_step_default_resources_table.sql');
          }
        }

        // Insert new default resources
        const defaultResourcesEntries: any[] = [];
        
        if (insertedSteps) {
          insertedSteps.forEach((insertedStep, index) => {
            const stepData = data.steps[index];
            if (stepData.default_resources && stepData.default_resources.length > 0) {
              stepData.default_resources.forEach(resource => {
                defaultResourcesEntries.push({
                  workflow_step_id: insertedStep.id,
                  resource_id: resource.id
                });
              });
            }
          });
        }

        if (defaultResourcesEntries.length > 0) {
          const { error: resourcesError } = await supabase
            .from('workflow_step_default_resources')
            .insert(defaultResourcesEntries);
          
          if (resourcesError) {
            console.error('Error inserting default resources:', resourcesError);
            // If the table doesn't exist yet, show a more helpful message
            if (resourcesError.message?.includes('does not exist')) {
              console.error('Junction table workflow_step_default_resources does not exist. Please run the migration: add_workflow_step_default_resources_table.sql');
            }
            // Don't fail the whole operation if default resources fail
          }
        }
      }
    }

    return workflow;
  } catch (error) {
    console.error('Error updating workflow:', error);
    throw error;
  }
}

export async function deleteWorkflow(workflowId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('service_workflows')
      .delete()
      .eq('id', workflowId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting workflow:', error);
    throw error;
  }
}